// src/pages/apikey/PermissionMatrix.tsx

import { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { useI18n, useApikey } from '@react/hooks';

const FALLBACK_SECTION_IDS = [
  'tasks', 'projects', 'documents', 'chat', 'forms', 'workflows',
  'billing', 'teams', 'calendar', 'notifications', 'users', 'apikeys',
] as const;

const FALLBACK_ACTION_IDS = ['view', 'create', 'edit', 'delete', 'admin'] as const;

export interface PermissionMatrixProps {
  value: Record<string, string[]>;
  onChange: (value: Record<string, string[]>) => void;
  disabled?: boolean;
}

interface Labeled { id: string; label: string }

/**
 * Sections/actions come from ListPermissions (server-defined, per OpenFGA object type).
 * Falls back to a static, i18n-labeled list while loading or if the call fails, so the picker
 * never blocks key creation on a network round trip.
 */
function usePermissionOptions(): { sections: Labeled[]; actions: Labeled[] } {
  const store = useApikey();
  const { t } = useI18n();
  const fallbackSections = FALLBACK_SECTION_IDS.map((id) => ({ id, label: t(`permissions.sections.${id}`) }));
  const fallbackActions = FALLBACK_ACTION_IDS.map((id) => ({ id, label: t(`permissions.actions.${id}`) }));
  const [sections, setSections] = useState<Labeled[]>(fallbackSections);
  const [actions, setActions] = useState<Labeled[]>(fallbackActions);

  useEffect(() => {
    let cancelled = false;
    store.ListPermissions()
      .then((resp) => {
        if (cancelled) return;
        const serverSections = resp.sections ?? [];
        if (serverSections.length === 0) return;
        setSections(serverSections.map((s) => ({ id: s.id, label: s.label || s.id })));
        const actionMap = new Map<string, string>();
        for (const s of serverSections) {
          for (const a of s.actions ?? []) actionMap.set(a.id, a.label || a.id);
        }
        if (actionMap.size > 0) {
          setActions([...actionMap.entries()].map(([id, label]) => ({ id, label })));
        }
      })
      .catch(() => {
        // keep fallback
      });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { sections, actions };
}

/**
 * Indeterminate-aware checkbox. Sets the indeterminate DOM property via ref.
 */
function IndeterminateCheckbox({
  indeterminate,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { indeterminate?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = !!indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      {...props}
      className={`h-4 w-4 rounded border-input text-primary focus:ring-ring ${props.disabled ? 'cursor-default opacity-60' : 'cursor-pointer'}`}
    />
  );
}

export function PermissionMatrix({ value, onChange, disabled }: PermissionMatrixProps) {
  const { t } = useI18n();
  const { sections, actions: actionOptions } = usePermissionOptions();
  const ALL_SECTION_IDS = useMemo(() => sections.map((s) => s.id), [sections]);
  const ALL_ACTION_IDS = useMemo(() => actionOptions.map((a) => a.id), [actionOptions]);
  // --- helpers ---

  const has = useCallback(
    (section: string, action: string): boolean => {
      return value[section]?.includes(action) ?? false;
    },
    [value],
  );

  const setPerms = useCallback(
    (next: Record<string, string[]>) => {
      if (disabled) return;
      // Clean up: remove empty arrays
      const cleaned: Record<string, string[]> = {};
      for (const [k, v] of Object.entries(next)) {
        if (v.length > 0) cleaned[k] = v;
      }
      onChange(cleaned);
    },
    [onChange, disabled],
  );

  /** Apply business rules: admin implies all; removing view clears the row. */
  const normalize = useCallback((_section: string, actions: string[]): string[] => {
    // If admin is present, all actions are implied
    if (actions.includes('admin')) {
      return [...ALL_ACTION_IDS];
    }
    // If view is not present, nothing is allowed
    if (!actions.includes('view')) {
      return [];
    }
    return actions;
  }, [ALL_ACTION_IDS]);

  // --- cell toggle ---

  const toggleCell = useCallback(
    (section: string, action: string) => {
      if (disabled) return;
      const current = value[section] ?? [];
      let next: string[];

      if (current.includes(action)) {
        next = current.filter((a) => a !== action);
      } else {
        next = [...current, action];
      }

      next = normalize(section, next);
      setPerms({ ...value, [section]: next });
    },
    [value, setPerms, normalize, disabled],
  );

  // --- row toggle ---

  const rowCheckedCount = useCallback(
    (section: string): number => {
      return (value[section] ?? []).length;
    },
    [value],
  );

  const isRowAllChecked = useCallback(
    (section: string): boolean => rowCheckedCount(section) === ALL_ACTION_IDS.length,
    [rowCheckedCount, ALL_ACTION_IDS],
  );

  const isRowPartial = useCallback(
    (section: string): boolean => {
      const count = rowCheckedCount(section);
      return count > 0 && count < ALL_ACTION_IDS.length;
    },
    [rowCheckedCount, ALL_ACTION_IDS],
  );

  const toggleRow = useCallback(
    (section: string) => {
      if (disabled) return;
      const allChecked = isRowAllChecked(section);
      const next = allChecked ? [] : [...ALL_ACTION_IDS];
      setPerms({ ...value, [section]: next });
    },
    [value, setPerms, isRowAllChecked, disabled, ALL_ACTION_IDS],
  );

  // --- column toggle ---

  const colCheckedCount = useCallback(
    (action: string): number => {
      return ALL_SECTION_IDS.filter((s) => has(s, action)).length;
    },
    [has, ALL_SECTION_IDS],
  );

  const isColAllChecked = useCallback(
    (action: string): boolean => colCheckedCount(action) === ALL_SECTION_IDS.length,
    [colCheckedCount, ALL_SECTION_IDS],
  );

  const isColPartial = useCallback(
    (action: string): boolean => {
      const count = colCheckedCount(action);
      return count > 0 && count < ALL_SECTION_IDS.length;
    },
    [colCheckedCount, ALL_SECTION_IDS],
  );

  const toggleColumn = useCallback(
    (action: string) => {
      if (disabled) return;
      const allChecked = isColAllChecked(action);
      const next = { ...value };

      for (const section of ALL_SECTION_IDS) {
        let current = next[section] ?? [];

        if (allChecked) {
          current = current.filter((a) => a !== action);
        } else {
          if (!current.includes(action)) {
            current = [...current, action];
          }
        }

        current = normalize(section, current);
        next[section] = current;
      }

      setPerms(next);
    },
    [value, setPerms, isColAllChecked, normalize, disabled, ALL_SECTION_IDS],
  );

  // --- full access toggle ---

  const totalChecked = useMemo(() => {
    return ALL_SECTION_IDS.reduce((sum, s) => sum + (value[s]?.length ?? 0), 0);
  }, [value, ALL_SECTION_IDS]);

  const totalPossible = ALL_SECTION_IDS.length * ALL_ACTION_IDS.length;
  const isFullAccess = totalChecked === totalPossible;
  const isPartialAccess = totalChecked > 0 && totalChecked < totalPossible;

  const toggleFullAccess = useCallback(() => {
    if (disabled) return;
    if (isFullAccess) {
      setPerms({});
    } else {
      const next: Record<string, string[]> = {};
      for (const s of ALL_SECTION_IDS) {
        next[s] = [...ALL_ACTION_IDS];
      }
      setPerms(next);
    }
  }, [isFullAccess, setPerms, disabled, ALL_SECTION_IDS, ALL_ACTION_IDS]);

  // --- render ---

  const cursorClass = disabled ? 'cursor-default' : 'cursor-pointer';

  return (
    <div className={`border border-border rounded-lg overflow-hidden ${disabled ? 'opacity-75' : ''}`}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-muted/50 backdrop-blur">
            <tr>
              {/* Top-left corner: full access */}
              <th className="text-left px-4 py-3 border-b border-border">
                <label className={`flex items-center gap-2 ${cursorClass}`}>
                  <IndeterminateCheckbox
                    checked={isFullAccess}
                    indeterminate={isPartialAccess}
                    onChange={toggleFullAccess}
                    disabled={disabled}
                  />
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t('permissions.fullAccess')}
                  </span>
                </label>
              </th>

              {/* Column headers */}
              {actionOptions.map((action) => (
                <th
                  key={action.id}
                  className="text-center px-3 py-3 border-b border-border"
                >
                  <label className={`flex flex-col items-center gap-1.5 ${cursorClass}`}>
                    <IndeterminateCheckbox
                      checked={isColAllChecked(action.id)}
                      indeterminate={isColPartial(action.id)}
                      onChange={() => toggleColumn(action.id)}
                      disabled={disabled}
                    />
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {action.label}
                    </span>
                  </label>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((section, idx) => (
              <tr
                key={section.id}
                className={`
                  hover:bg-muted/50 transition-colors
                  ${idx % 2 === 0 ? 'bg-muted/30' : ''}
                `}
              >
                {/* Row header: toggle all in section */}
                <td className="px-4 py-2.5 border-b border-border/50">
                  <label className={`flex items-center gap-2 ${cursorClass}`}>
                    <IndeterminateCheckbox
                      checked={isRowAllChecked(section.id)}
                      indeterminate={isRowPartial(section.id)}
                      onChange={() => toggleRow(section.id)}
                      disabled={disabled}
                    />
                    <span className="text-sm font-medium text-foreground">
                      {section.label}
                    </span>
                  </label>
                </td>

                {/* Action cells */}
                {actionOptions.map((action) => (
                  <td
                    key={action.id}
                    className="text-center px-3 py-2.5 border-b border-border/50"
                  >
                    <label className={`inline-flex items-center justify-center ${cursorClass}`}>
                      <span className="sr-only">{`${section.label} — ${action.label}`}</span>
                      <IndeterminateCheckbox
                        checked={has(section.id, action.id)}
                        onChange={() => toggleCell(section.id, action.id)}
                        disabled={disabled}
                      />
                    </label>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PermissionMatrix;
